import { ActionIcon, Box, Button, Checkbox, Group, Loader, Menu, Text, TextInput, Tooltip } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { useDebouncedCallback, useDocumentTitle, useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconColumns3,
  IconDownload,
  IconInbox,
  IconPlus,
  IconPrinter,
  IconRefresh,
  IconSearch,
  IconSearchOff,
  IconSend,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  appBase,
  dayjs,
  docStatusOf,
  type FilterValue,
  formatDate,
  type ListParams,
  type Locale,
  labels,
  PAGE_SIZES,
  type RefMap,
  type ResolvedField,
  type Resource,
  today,
} from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { DataGrid, type DataTableColumn, EmptyState, FilterChips, Page, RecordCard, RecordCards } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { useDataTableColumns } from 'mantine-datatable'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '../api/client'
import { type Rec, resourceApi, useDeleteRecord, useLifecycle, useResourceList } from '../api/resources'
import { useNavigate } from '../app/scope'
import { useCan, usePersonal, useSession } from '../auth/session'
import { GuideButton } from '../guides/guides'
import { PersonAvatar } from '../photo/photos'
import { usePreferences } from '../prefs/preferences'
import { PrintArea, PrintableList, usePrint } from '../print/print'
import { type ListChip, pagePath, type ResourcePage } from './config'
import { exportFields, exportRecords, fetchAll } from './dataio'
import { FieldValue, type FieldValueContext, fieldText } from './FieldValue'
import { FilterMenu } from './filters'
import { ImportWizard } from './ImportWizard'
import { useListParams } from './useListParams'

const PHONE = '(max-width: 47.99em)'
const AVATAR_TITLE = new Set(['accounting.customer', 'tms.driver', 'admin.user', 'hr.employee'])

/**
 * The list screen every resource gets for free: filter chips with counts,
 * search, filters, sortable/toggleable columns, selection, import, export
 * and print on desktop; cards with "load more" on phones.
 */
/** Buttons in the bar that replaces the toolbar while rows are selected; pages' `bulkActions` use it too */
export const bulkButton = { size: 'md', variant: 'light', radius: 'xl' } as const

export function ResourceListPage({ page }: { page: ResourcePage }) {
  const { resource, list: opts = {} } = page
  const path = pagePath(resource)
  const { t, i18n } = useTranslation()
  const { appId } = useSession()
  const locale = currentLocale(i18n)
  const can = useCan()
  const phone = useMediaQuery(PHONE, false, { getInitialValueInEffect: false })
  const navigate = useNavigate()
  const { prefs } = usePreferences()
  const [chosen, setParams, sortChosen] = useListParams(opts.defaultParams ?? { sort: resource.defaultSort })
  // In Thananan OS a list is one's own, whatever else one may read (the server applies it within one's rights)
  const inPersonalApp = usePersonal()
  const personal = inPersonalApp && resource.mine.length > 0
  const params = useMemo(() => (personal ? { ...chosen, mine: true } : chosen), [chosen, personal])
  // Bringing in rows from a file is work-app work, not Thananan OS's
  const mayImport = !inPersonalApp && can('import', resource.key) && resource.actions.includes('import')
  const [selected, setSelected] = useState<Rec[]>([])
  const selectable =
    can('delete', resource.key) ||
    can('export', resource.key) ||
    (!!resource.submittable && can('submit', resource.key)) ||
    !!opts.bulkActions
  const life = useLifecycle(resource)
  const [importOpen, setImportOpen] = useState(false)
  const [busy, setBusy] = useState<null | 'export' | 'print'>(null)
  const [printData, setPrintData] = useState<{ items: Rec[]; refs: RefMap } | null>(null)
  const title = t(labels.plural(resource))
  useDocumentTitle(`${title} · ${t(`apps.${appId}`)}`)

  const chipsFieldName =
    opts.chipsField === false ? undefined : (opts.chipsField ?? (resource.field('status')?.tones ? 'status' : undefined))
  const chipsField = chipsFieldName ? resource.field(chipsFieldName) : undefined
  // Phones page through cards on their own; this request then only brings the chip counts and the total
  const query = useResourceList(resource, { ...params, ...(phone && { pageSize: 1 }), facets: chipsField ? [chipsField.name] : undefined })
  const data = query.data
  const refs = data?.refs ?? {}
  const ctx: FieldValueContext = { resource, refs, t, locale }
  const rowHref = opts.rowHref === false ? undefined : (opts.rowHref ?? ((r: Rec) => `/${path}/${r.id}`))

  const combine = opts.combine ?? {}
  const columnFields = useMemo(() => {
    const inside = new Set(Object.values(opts.combine ?? {}).flat())
    return (opts.columns ?? resource.columns)
      .filter((n) => !inside.has(n))
      .map((n) => resource.field(n))
      .filter((f): f is ResolvedField => !!f && f.list !== false)
  }, [opts.columns, opts.combine, resource])
  const columns = useMemo<DataTableColumn<Rec>[]>(
    () =>
      columnFields.map((f) => {
        const list = f.list || {}
        return {
          accessor: f.name,
          title: t(opts.columnTitles?.[f.name] ?? f.label),
          width: list.width,
          textAlign: list.align === 'end' ? 'right' : list.align === 'center' ? 'center' : 'left',
          sortable: !['json', 'multiselect', 'tags'].includes(f.type),
          // shown/hidden from the columns menu (the header toggle icons have no labels)
          resizable: true,
          defaultToggle: !list.hidden,
          // 1: always (tablets too), 2: desktop, 3: wide screens. Phones show cards instead.
          visibleMediaQuery: list.priority === 3 ? '(min-width: 75em)' : list.priority === 2 ? '(min-width: 62em)' : undefined,
          render: (r: Rec) => {
            const custom = opts.render?.[f.name]
            if (custom) return custom(r, refs)
            // A draft document has no number yet
            if (f.name === resource.title && r[f.name] == null && resource.submittable && docStatusOf(r) === 'draft') {
              return (
                <Text span size="sm" c="dimmed">
                  {t('doc.status.draft')}
                </Text>
              )
            }
            const value = <FieldValue field={f} value={r[f.name]} ctx={{ resource, refs, t, locale }} />
            return f.name === resource.title ? (
              <Text span fw={600} size="sm">
                {value}
              </Text>
            ) : (
              value
            )
          },
        } satisfies DataTableColumn<Rec>
      }),
    [columnFields, t, opts.render, opts.columnTitles, refs, resource, locale],
  )
  // The default order shows no arrow, so a column's third click visibly returns to it
  const sortStatus = sortChosen ? sortStatusOf(params.sort) : sortStatusOf(undefined)
  const columnsState = useDataTableColumns<Rec>({ key: `tnn.cols.${resource.key}`, columns })
  // Columns keep the order set in code; each browser remembers only which are shown and their widths.
  // The table also stores an order of its own (and applies it), so it is put back to the code's order.
  const position = new Map(columns.map((c, i) => [c.accessor, i]))
  const shownColumns = [...columnsState.effectiveColumns].sort((a, b) => (position.get(a.accessor) ?? 0) - (position.get(b.accessor) ?? 0))
  const codeOrder = columns.map((c) => String(c.accessor)).join(',')
  const { columnsOrder, setColumnsOrder } = columnsState
  useEffect(() => {
    if (columnsOrder.join(',') !== codeOrder) setColumnsOrder(codeOrder.split(','))
  }, [columnsOrder, codeOrder, setColumnsOrder])

  const hasFilters = !!params.q || Object.keys(params.filters).length > 0
  // The chips already offer "all", so only search and menu filters need a clear button
  const chipKeys = new Set([
    chipsField?.name,
    opts.dateFilter,
    ...(opts.extraChips ?? []).flatMap((c) => Object.keys(c.params.filters ?? {})),
  ])
  const dateField = opts.dateFilter ? resource.field(opts.dateFilter) : undefined
  const canClear = !!params.q || Object.keys(params.filters).some((k) => !chipKeys.has(k))
  const chipOptions = buildChips(resource, chipsField, opts.extraChips, data?.facets?.[chipsField?.name ?? ''], t)
  const activeChip = chipOptions.find((c) => matchesChip(c, params))?.value ?? ''

  const del = useDeleteRecord(resource)
  const askDelete = (records: Rec[]) =>
    modals.openConfirmModal({
      title: t('confirm.deleteTitle', { name: t(labels.title(resource)) }),
      children: (
        <Text size="sm">
          {t('confirm.deleteBody', {
            label: records.map((r) => fieldText(resource.field(resource.title) as ResolvedField, r[resource.title], ctx)).join(', '),
          })}
        </Text>
      ),
      labels: { confirm: t('actions.delete'), cancel: t('actions.cancel') },
      confirmProps: { color: 'danger' },
      onConfirm: async () => {
        for (const r of records) await del.mutateAsync(r.id)
        setSelected([])
        notifications.show({ message: t('status.deleted', { name: t(labels.title(resource)) }) })
      },
    })

  // Documents: submit the selected drafts one by one, like Frappe's bulk submit; refused ones say why
  const drafts = resource.submittable ? selected.filter((r) => docStatusOf(r) === 'draft') : []
  const canSubmitMany = !!resource.submittable && can('submit', resource.key) && drafts.length > 0
  // Only drafts can be deleted; submitted documents are cancelled instead
  const canDeleteSelected = can('delete', resource.key) && (!resource.submittable || drafts.length === selected.length)
  const askSubmit = (records: Rec[]) =>
    modals.openConfirmModal({
      title: t('doc.confirm.submitManyTitle', { name: t(labels.title(resource)), count: records.length }),
      children: <Text size="sm">{t('doc.confirm.submitBody')}</Text>,
      labels: { confirm: t('doc.actions.submit'), cancel: t('actions.back') },
      onConfirm: async () => {
        let done = 0
        const failed: string[] = []
        for (const r of records) {
          try {
            await life.submit.mutateAsync(r.id)
            done++
          } catch (error) {
            failed.push(isApiError(error) ? error.message : t('errors.generic'))
          }
        }
        setSelected([])
        notifications.show(
          failed.length
            ? { color: 'danger', message: t('doc.done.submitManyFailed', { done, failed: failed.length, reason: failed[0] }) }
            : { color: 'primary', message: t('doc.done.submitMany', { count: done }) },
        )
      },
    })

  const visibleFieldNames = shownColumns
    .filter((c) => !c.hidden)
    .flatMap((c) => [String(c.accessor), ...(combine[String(c.accessor)] ?? [])])

  const runExport = async (format: 'xlsx' | 'csv', only?: Rec[]) => {
    setBusy('export')
    try {
      const { items, refs: allRefs } = only ? { items: only, refs } : await fetchAll(resource, params)
      await exportRecords({
        resource,
        fields: exportFields(resource, visibleFieldNames),
        records: items,
        ctx: { ...ctx, refs: allRefs },
        format,
        fileName: title,
      })
      notifications.show({ message: t('export.ready', { count: items.length }) })
    } finally {
      setBusy(null)
    }
  }

  const printer = usePrint({ title, landscape: true, pageLabel: t('print.page') })
  const runPrint = async () => {
    setBusy('print')
    try {
      setPrintData(await fetchAll(resource, params))
      requestAnimationFrame(() => printer.print())
    } finally {
      setBusy(null)
    }
  }

  const headerActions = (
    <>
      {opts.actions}
      {!phone && mayImport && <IconAction label={t('actions.import')} icon={IconUpload} onClick={() => setImportOpen(true)} />}
      {can('export', resource.key) && (
        <Menu>
          <Menu.Target>
            <span>
              <IconAction label={t('actions.export')} icon={IconDownload} loading={busy === 'export'} />
            </span>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t('export.scopeFiltered', { count: data?.total ?? 0 })}</Menu.Label>
            <Menu.Item onClick={() => runExport('xlsx')}>{t('export.excel')}</Menu.Item>
            <Menu.Item onClick={() => runExport('csv')}>{t('export.csv')}</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
      {!phone && can('print', resource.key) && resource.actions.includes('print') && (
        <IconAction label={t('print.list')} icon={IconPrinter} onClick={runPrint} loading={busy === 'print'} />
      )}
      {!phone && <ColumnsMenu state={columnsState} columns={columns} t={t} />}
      {!phone && (
        <IconAction
          label={t('actions.refresh')}
          icon={IconRefresh}
          onClick={() => query.refetch()}
          loading={query.isFetching && !query.isPending}
        />
      )}
      <GuideButton resource={resource} />
      {opts.createInHeader !== false && can('create', resource.key) && (
        <Button leftSection={<IconPlus size={18} />} variant="light" onClick={() => navigate(`/${path}/new`)} ml={4}>
          {t(`${resource.app}:${resource.name}.create`, { defaultValue: t('actions.create') })}
        </Button>
      )}
    </>
  )

  // Chips stay while rows are ticked, so the table doesn't jump; picking one lets go of the ticks
  const chips = chipOptions.length > 0 && (
    <FilterChips
      options={chipOptions}
      value={activeChip}
      onChange={(v) => {
        const chip = chipOptions.find((c) => c.value === v)
        const filters = { ...params.filters }
        if (chipsField) delete filters[chipsField.name]
        for (const c of opts.extraChips ?? []) for (const k of Object.keys(c.params.filters ?? {})) delete filters[k]
        setParams({ filters: { ...filters, ...(chip?.params.filters ?? {}) } })
        setSelected([])
      }}
    />
  )

  const toolbar = (
    <>
      {dateField && <DayFilter field={dateField} params={params} setParams={setParams} t={t} locale={locale} />}
      <SearchBox
        value={params.q ?? ''}
        onChange={(q) => setParams({ q: q || undefined })}
        placeholder={t('list.searchPlaceholder', { name: title })}
      />
      <FilterMenu
        resource={resource}
        exclude={chipsField?.name}
        countExcept={opts.dateFilter}
        params={params}
        setParams={setParams}
        t={t}
        locale={locale}
      />
      {canClear && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconX size={16} />}
          onClick={() => setParams({ q: undefined, filters: {} })}
        >
          {t('actions.clearFilters')}
        </Button>
      )}
    </>
  )

  const selectionBar = selected.length > 0 && (
    <Group gap="sm" wrap="wrap" mih={42}>
      <Checkbox
        checked
        indeterminate={selected.length < (data?.items.length ?? 0)}
        onChange={() => setSelected([])}
        aria-label={t('actions.selectAll')}
      />
      <Text size="md" fw={500} mr="xs">
        {t('list.selected', { count: selected.length })}
      </Text>
      {can('export', resource.key) && (
        <Button {...bulkButton} leftSection={<IconDownload size={18} />} onClick={() => runExport('xlsx', selected)}>
          {t('actions.export')}
        </Button>
      )}
      {opts.bulkActions?.(selected, () => setSelected([]))}
      {canSubmitMany && (
        <Button {...bulkButton} leftSection={<IconSend size={18} />} onClick={() => askSubmit(drafts)}>
          {t('doc.actions.submit')} ({drafts.length})
        </Button>
      )}
      {canDeleteSelected && (
        <Button {...bulkButton} color="danger" leftSection={<IconTrash size={18} />} onClick={() => askDelete(selected)}>
          {t('actions.delete')}
        </Button>
      )}
    </Group>
  )

  const empty = hasFilters ? (
    <EmptyState
      icon={IconSearchOff}
      title={t('empty.noResults')}
      body={t('empty.noResultsBody')}
      actions={
        <Button variant="light" onClick={() => setParams({ q: undefined, filters: {} })}>
          {t('actions.clearFilters')}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={IconInbox}
      title={t('empty.title', { name: title })}
      body={t(mayImport ? 'empty.body' : 'empty.bodyCreate')}
      actions={
        <Group gap="sm">
          {can('create', resource.key) && (
            <Button leftSection={<IconPlus size={18} />} onClick={() => navigate(`/${path}/new`)}>
              {t(`${resource.app}:${resource.name}.create`, { defaultValue: t('actions.create') })}
            </Button>
          )}
          {mayImport && (
            <Button variant="default" leftSection={<IconUpload size={18} />} onClick={() => setImportOpen(true)}>
              {t('actions.import')}
            </Button>
          )}
          {opts.emptyActions}
        </Group>
      }
    />
  )

  return (
    <Page
      title={title}
      actions={headerActions}
      toolbar={
        <>
          {chips}
          {selectionBar || toolbar}
        </>
      }
    >
      {phone ? (
        <PhoneList page={page} params={params} ctx={ctx} rowHref={rowHref} empty={empty} />
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataGrid<Rec>
            density={prefs.density}
            records={data?.items ?? []}
            columns={shownColumns}
            storeColumnsKey={`tnn.cols.${resource.key}`}
            fetching={query.isFetching}
            totalRecords={data?.total ?? 0}
            recordsPerPage={params.pageSize}
            page={params.page}
            onPageChange={(p) => setParams({ page: p })}
            recordsPerPageOptions={[...PAGE_SIZES]}
            onRecordsPerPageChange={(n) => setParams({ pageSize: n })}
            recordsPerPageLabel={t('list.rowsPerPage')}
            allRecordsSelectionCheckboxProps={{ 'aria-label': t('actions.selectAll') }}
            getRecordSelectionCheckboxProps={(_, i) => ({ 'aria-label': t('list.selectRow', { n: i + 1 }) })}
            getPaginationControlProps={(control) =>
              control === 'previous'
                ? { 'aria-label': t('list.previousPage') }
                : control === 'next'
                  ? { 'aria-label': t('list.nextPage') }
                  : {}
            }
            paginationText={({ from, to, totalRecords }) => t('list.range', { from, to, total: totalRecords })}
            sortStatus={sortStatus}
            onSortStatusChange={(s) => {
              const key = String(s.columnAccessor)
              // Three steps per column: ascending → descending → back to the list's default order
              if (sortStatus.columnAccessor === key && sortStatus.direction === 'desc') return setParams({ sort: undefined })
              const dir = s.direction === 'desc' ? '-' : ''
              setParams({ sort: [key, ...(combine[key] ?? [])].map((k) => dir + k).join(',') })
            }}
            selectedRecords={selectable ? selected : undefined}
            onSelectedRecordsChange={selectable ? setSelected : undefined}
            onRowClick={
              rowHref
                ? ({ record, event }) => {
                    // Clicks inside a dialog opened from a cell reach the row through React's portal bubbling
                    if (!event.currentTarget.contains(event.target as Node)) return
                    if (event.metaKey || event.ctrlKey) window.open(appBase(appId) + rowHref(record), '_blank')
                    else navigate(rowHref(record))
                  }
                : undefined
            }
            customRowAttributes={() => (rowHref ? { 'data-clickable': true } : {})}
            // mantine-datatable keeps the empty state in the page (transparent) when there are rows
            emptyState={data && data.items.length === 0 ? empty : <span />}
            minHeight={data && data.total === 0 ? 360 : undefined}
            height="100%"
          />
        </div>
      )}
      {printData && (
        <PrintArea printRef={printer.ref}>
          <PrintableList
            title={title}
            subtitle={params.q ? `“${params.q}”` : undefined}
            fields={exportFields(resource, visibleFieldNames)}
            records={printData.items}
            ctx={{ ...ctx, refs: printData.refs }}
          />
        </PrintArea>
      )}
      {importOpen && <ImportWizard resource={resource} opened={importOpen} onClose={() => setImportOpen(false)} />}
    </Page>
  )
}

/**
 * The header arrow for the current sort. A mixed sort such as `-pickupDate,pickupTime` (newest day,
 * earliest time) gets no arrow, since one arrow on the column would contradict half of it.
 */
function sortStatusOf(sort?: string) {
  const keys = sort?.split(',').filter(Boolean) ?? []
  const first = keys[0]
  const mixed = keys.some((k) => k.startsWith('-') !== first?.startsWith('-'))
  if (!first || mixed) return { columnAccessor: '', direction: 'asc' as const }
  return first.startsWith('-')
    ? { columnAccessor: first.slice(1), direction: 'desc' as const }
    : { columnAccessor: first, direction: 'asc' as const }
}

function buildChips(
  resource: Resource,
  field: ResolvedField | undefined,
  extra: ListChip[] | undefined,
  facet: Record<string, number> | undefined,
  t: TFunction,
): (ListChip & { count?: number })[] {
  const extraChips = (extra ?? []).map((chip) => ({ ...chip, label: t(chip.label) }))
  if (!field) return extraChips
  const values = field.options ?? []
  const total = facet ? Object.values(facet).reduce((a, b) => a + b, 0) : undefined
  return [
    { value: '', label: t('list.all'), params: {}, count: total },
    ...values.map((v) => ({
      value: v,
      label: t(labels.option(resource, field.name, v)),
      params: { filters: { [field.name]: { op: 'in', values: [v] } as FilterValue } },
      count: facet?.[v] ?? (facet ? 0 : undefined),
      tone: field.tones?.[v],
    })),
    ...extraChips,
  ]
}

function matchesChip(chip: ListChip, params: ListParams): boolean {
  const chipFilters = chip.params.filters ?? {}
  const keys = Object.keys(chipFilters)
  if (keys.length === 0) return false
  return keys.every((k) => JSON.stringify(params.filters[k]) === JSON.stringify(chipFilters[k]))
}

function IconAction({
  label,
  icon: Icon,
  onClick,
  loading,
}: {
  label: string
  icon: typeof IconSearch
  onClick?: () => void
  loading?: boolean
}) {
  return (
    <Tooltip label={label}>
      <ActionIcon size={40} onClick={onClick} aria-label={label} loading={loading}>
        <Icon size={22} stroke={1.75} />
      </ActionIcon>
    </Tooltip>
  )
}

const iso = (d: ReturnType<typeof dayjs>) => d.format('YYYY-MM-DD')

/** The day a list is showing, always in view: today, the last 7 days, between two days picked on a calendar, or every day. */
function DayFilter({
  field,
  params,
  setParams,
  t,
  locale,
}: {
  field: ResolvedField
  params: ListParams
  setParams: (patch: Partial<ListParams>) => void
  t: TFunction
  locale: Locale
}) {
  const [opened, setOpened] = useState(false)
  const [between, setBetween] = useState<[string | null, string | null] | null>(null)
  const presets = [
    { key: 'today', label: t('time.today'), from: 'today', to: 'today' },
    { key: 'week', label: t('list.last7Days'), from: iso(dayjs().subtract(6, 'day')), to: 'today' },
  ]
  const value = params.filters[field.name]
  const range = value?.op === 'range' ? value : undefined
  const preset = range && presets.find((p) => p.from === range.from && p.to === range.to)
  // `today` / `now` are URL tokens the API resolves
  const day = (v?: string) => (v === 'today' || v === 'now' ? today() : (v ?? null))
  const show = (v?: string) => formatDate(day(v) ?? undefined, { locale })
  const label = preset ? preset.label : range ? `${show(range.from)} – ${show(range.to)}` : t('list.allDates')
  const pick = (next?: FilterValue) => {
    const { [field.name]: _, ...rest } = params.filters
    setParams({ filters: next ? { ...rest, [field.name]: next } : rest })
  }
  const check = (on: boolean) => (on ? <IconCheck size={16} /> : null)
  return (
    <Menu
      position="bottom-start"
      opened={opened}
      onChange={(o) => {
        setOpened(o)
        if (!o) setBetween(null)
      }}
    >
      <Menu.Target>
        <Button
          variant="default"
          size="sm"
          radius="sm"
          flex="none"
          leftSection={<IconCalendar size={18} stroke={1.75} />}
          rightSection={<IconChevronDown size={14} />}
          aria-label={`${t(field.label)}: ${label}`}
        >
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {between ? (
          // The menu's own arrow keys would take focus off the calendar's days
          <Box p={4} onKeyDown={(e) => e.stopPropagation()}>
            <DatePicker
              type="range"
              allowSingleDateInRange
              defaultDate={between[0] ?? undefined}
              value={between}
              onChange={([from, to]) => {
                setBetween([from, to])
                if (from && to) {
                  pick({ op: 'range', from, to })
                  setOpened(false)
                  setBetween(null)
                }
              }}
            />
          </Box>
        ) : (
          <>
            {presets.map((p) => (
              <Menu.Item
                key={p.key}
                rightSection={check(preset?.key === p.key)}
                onClick={() => pick({ op: 'range', from: p.from, to: p.to })}
              >
                {p.label}
              </Menu.Item>
            ))}
            <Menu.Item
              rightSection={check(!!range && !preset)}
              closeMenuOnClick={false}
              onClick={() => setBetween(range && !preset ? [day(range.from), day(range.to)] : [null, null])}
            >
              {t('list.between')}
            </Menu.Item>
            <Menu.Item rightSection={check(!range)} onClick={() => pick()}>
              {t('list.allDates')}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [text, setText] = useState(value)
  const debounced = useDebouncedCallback(onChange, 300)
  return (
    <TextInput
      value={text}
      onChange={(e) => {
        setText(e.currentTarget.value)
        debounced(e.currentTarget.value.trim())
      }}
      placeholder={placeholder}
      leftSection={<IconSearch size={18} stroke={1.75} />}
      radius="xl"
      size="sm"
      flex="1 0 200px"
      maw={300}
      aria-label={placeholder}
    />
  )
}

function ColumnsMenu({
  state,
  columns,
  t,
}: {
  state: ReturnType<typeof useDataTableColumns<Rec>>
  columns: DataTableColumn<Rec>[]
  t: TFunction
}) {
  return (
    <Menu closeOnItemClick={false}>
      <Menu.Target>
        <span>
          <IconAction label={t('actions.columns')} icon={IconColumns3} />
        </span>
      </Menu.Target>
      <Menu.Dropdown>
        {columns.map((col) => {
          const toggled = state.columnsToggle.find((x) => x.accessor === col.accessor)?.toggled ?? true
          return (
            <Menu.Item
              key={col.accessor}
              leftSection={<Checkbox size="xs" checked={toggled} readOnly tabIndex={-1} />}
              onClick={() =>
                state.setColumnsToggle(state.columnsToggle.map((x) => (x.accessor === col.accessor ? { ...x, toggled: !toggled } : x)))
              }
            >
              {col.title as ReactNode}
            </Menu.Item>
          )
        })}
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            state.resetColumnsToggle()
            state.resetColumnsWidth()
          }}
        >
          {t('list.resetColumns')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

/** Phones: records as cards, loading more at the end of the list. */
function PhoneList({
  page,
  params,
  ctx,
  rowHref,
  empty,
}: {
  page: ResourcePage
  params: ListParams
  ctx: FieldValueContext
  rowHref?: (r: Rec) => string
  empty: ReactNode
}) {
  const { resource, list: opts = {} } = page
  const { t } = ctx
  const query = useInfiniteQuery({
    queryKey: ['r', resource.key, 'cards', { ...params, page: undefined }],
    queryFn: ({ pageParam }) => resourceApi(resource.key).list({ ...params, page: pageParam, pageSize: 25 }),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (all.reduce((n, p) => n + p.items.length, 0) < last.total ? all.length + 1 : undefined),
  })
  const pages = query.data?.pages ?? []
  const items = pages.flatMap((p) => p.items)
  const refs: RefMap = Object.assign({}, ...pages.map((p) => p.refs))
  const c = { ...ctx, refs }
  const card = resource.card

  if (query.isPending) return <Loader type="dots" mx="auto" my="xl" />
  if (items.length === 0) return <>{empty}</>

  return (
    <RecordCards>
      {items.map((r) => {
        if (opts.card) return <RecordCard key={r.id} href={rowHref?.(r)} {...opts.card(r, refs)} />
        const titleField = resource.field(card?.title ?? resource.title) as ResolvedField
        const statusField = card?.status ? resource.field(card.status) : undefined
        const subtitleField = card?.subtitle ? resource.field(card.subtitle) : undefined
        const titleText = fieldText(titleField, r[titleField.name], c)
        return (
          <RecordCard
            key={r.id}
            href={rowHref?.(r)}
            title={titleText}
            subtitle={subtitleField ? fieldText(subtitleField, r[subtitleField.name], c) : undefined}
            avatar={
              card?.photo ? (
                <PersonAvatar name={titleText} size={36} fileId={(r[card.photo] as string | null) ?? null} />
              ) : titleField.ref && AVATAR_TITLE.has(titleField.ref.resource) ? (
                <PersonAvatar
                  name={titleText}
                  size={36}
                  userId={titleField.ref.resource === 'admin.user' ? String(r[titleField.name] ?? '') : null}
                  employeeId={titleField.ref.resource === 'hr.employee' ? String(r[titleField.name] ?? '') : null}
                />
              ) : undefined
            }
            status={statusField ? <FieldValue field={statusField} value={r[statusField.name]} ctx={c} /> : undefined}
            meta={(card?.meta ?? [])
              .map((m) => resource.field(m))
              .filter((f): f is ResolvedField => !!f)
              .map((f) => fieldText(f, r[f.name], c))
              .filter(Boolean)}
          />
        )
      })}
      {query.hasNextPage && (
        <Button variant="light" onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage} mx="auto" mt="sm">
          {t('actions.more')}
        </Button>
      )}
    </RecordCards>
  )
}
